import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { Portal } from '../components/Portal';
import { useTenant } from '../contexts/TenantContext';
import { useT } from '../i18n';

/**
 * Smart follow-up: escolher um período, ver quem falou com a empresa nele, deixar a IA escrever as
 * mensagens (por cliente ou uma vez para todos), revisar e enviar com intervalos aleatórios.
 */

type Status = 'DRAFT' | 'GENERATING' | 'READY' | 'SENDING' | 'PAUSED' | 'DONE' | 'CANCELLED';

interface FollowUpItem {
  id: string;
  phone: string;
  contactName: string | null;
  sessionName: string | null;
  sourceSessionName: string | null;
  lastInboundAt: string;
  lastInboundText: string | null;
  included: boolean;
  status: string;
  messages: string[] | null;
  nextIndex: number;
  skipReason: string | null;
  error: string | null;
}

interface FollowUp {
  id: string;
  name: string;
  status: Status;
  mode: 'PERSONALIZED' | 'SHARED';
  periodFrom: string;
  periodTo: string;
  periodRanges: { from: string; to: string }[] | null;
  lastWordFromCustomer: boolean;
  aiProvider: 'OPENAI' | 'GROQ' | 'CLAUDE';
  aiModel: string;
  instructions: string;
  greeting: string;
  messageCount: number;
  maxDelaySeconds: number;
  sendFromSessionName: string | null;
  fallbackSessionName: string | null;
  sharedMessages: string[] | null;
  waitingReason: string | null;
  createdAt: string;
  counts?: Record<string, number>;
  items?: FollowUpItem[];
}

interface Session { name: string; displayName?: string; provider?: string }

type PeriodMode = 'DAYS_AGO' | 'DATE' | 'DATES' | 'RANGE';

interface FormState {
  name: string;
  mode: 'PERSONALIZED' | 'SHARED';
  // Quem entra: a ÚLTIMA mensagem do cliente caiu num dia (N dias atrás ou uma data) ou numa faixa
  periodMode: PeriodMode;
  daysAgo: number;
  day: string; // YYYY-MM-DD
  days: string[]; // vários dias soltos (YYYY-MM-DD)
  // Só conversas em que a última mensagem é do cliente (ninguém respondeu depois)
  lastWordFromCustomer: boolean;
  periodFrom: string;
  periodTo: string;
  aiProvider: 'OPENAI' | 'GROQ' | 'CLAUDE';
  aiModel: string;
  instructions: string;
  greeting: string;
  messageCount: number;
  maxDelayMinutes: number;
  sendFromSessionName: string;
  fallbackSessionName: string;
}

const STATUS_STYLE: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  GENERATING: 'bg-purple-100 text-purple-800',
  READY: 'bg-blue-100 text-blue-800',
  SENDING: 'bg-green-100 text-green-800',
  PAUSED: 'bg-yellow-100 text-yellow-800',
  DONE: 'bg-emerald-100 text-emerald-800',
  CANCELLED: 'bg-red-100 text-red-700',
  PENDING: 'bg-gray-100 text-gray-600',
  QUEUED: 'bg-blue-50 text-blue-700',
  SENT: 'bg-emerald-100 text-emerald-800',
  REPLIED: 'bg-teal-100 text-teal-800',
  SKIPPED: 'bg-gray-100 text-gray-500',
  FAILED: 'bg-red-100 text-red-700',
};

// datetime-local quer "YYYY-MM-DDTHH:mm" no horário local
function toLocalInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function toDateInput(date: Date): string {
  return toLocalInput(date).slice(0, 10);
}

function dayRange(day: string): { from: Date; to: Date } {
  const from = new Date(`${day}T00:00:00`);
  const to = new Date(`${day}T23:59:59.999`);
  return { from, to };
}

function daysAgoDate(days: number): string {
  return toDateInput(new Date(Date.now() - days * 86400000));
}

/** O período que vai para o servidor, a partir do modo escolhido */
function resolvePeriod(form: FormState): { from: Date; to: Date; ranges?: { from: Date; to: Date }[] } {
  if (form.periodMode === 'DATES') {
    const ranges = [...form.days].sort().map(dayRange);
    return { from: ranges[0]?.from ?? new Date(NaN), to: ranges[ranges.length - 1]?.to ?? new Date(NaN), ranges };
  }
  if (form.periodMode === 'DAYS_AGO') return dayRange(daysAgoDate(form.daysAgo));
  if (form.periodMode === 'DATE') return dayRange(form.day);
  return { from: new Date(form.periodFrom), to: new Date(form.periodTo) };
}

// Mensagens prontas para enviar a todos, sem passar pela IA ({{name}} vira o primeiro nome)
const READY_MESSAGES = [
  {
    key: 'hours',
    text: 'أهلاً {{name}} 👋\nآسفين على التأخير في الرد 🙏\nحبينا نقولك إن مطعمنا شغال كل يوم من الساعة 2 الضهر لحد الساعة 4 الفجر ⏰\nلو لسه حابب تطلب، ابعتلنا طلبك وعنوانك وهنجهزهولك على طول 🛵',
  },
];

function emptyForm(): FormState {
  const now = new Date();
  return {
    name: '',
    mode: 'PERSONALIZED',
    periodMode: 'DAYS_AGO',
    daysAgo: 7,
    day: daysAgoDate(7),
    days: [],
    lastWordFromCustomer: true,
    periodFrom: toLocalInput(new Date(now.getTime() - 7 * 86400000)),
    periodTo: toLocalInput(now),
    aiProvider: 'OPENAI',
    aiModel: 'gpt-4o-mini',
    instructions: '',
    greeting: '',
    messageCount: 1,
    maxDelayMinutes: 7,
    sendFromSessionName: '',
    fallbackSessionName: '',
  };
}

function formFrom(followUp: FollowUp): FormState {
  const from = new Date(followUp.periodFrom);
  const to = new Date(followUp.periodTo);
  // Um dia inteiro volta como "data"; o resto, como faixa
  const singleDay = toLocalInput(from).endsWith('T00:00') && toDateInput(from) === toDateInput(to)
    && to.getHours() === 23 && to.getMinutes() === 59;
  return {
    name: followUp.name,
    mode: followUp.mode,
    periodMode: followUp.periodRanges?.length ? 'DATES' : singleDay ? 'DATE' : 'RANGE',
    daysAgo: 7,
    day: toDateInput(from),
    days: (followUp.periodRanges || []).map(r => toDateInput(new Date(r.from))),
    lastWordFromCustomer: followUp.lastWordFromCustomer,
    periodFrom: toLocalInput(new Date(followUp.periodFrom)),
    periodTo: toLocalInput(new Date(followUp.periodTo)),
    aiProvider: followUp.aiProvider,
    aiModel: followUp.aiModel,
    instructions: followUp.instructions,
    greeting: followUp.greeting,
    messageCount: followUp.messageCount,
    maxDelayMinutes: Math.round(followUp.maxDelaySeconds / 60),
    sendFromSessionName: followUp.sendFromSessionName || '',
    fallbackSessionName: followUp.fallbackSessionName || '',
  };
}

export function FollowUpsPage() {
  const t = useT();
  const { selectedTenantId } = useTenant();
  const [list, setList] = useState<FollowUp[]>([]);
  const [current, setCurrent] = useState<FollowUp | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [models, setModels] = useState<Record<string, string[]>>({ OPENAI: ['gpt-4o-mini'], GROQ: ['llama-3.3-70b-versatile'] });
  const [form, setForm] = useState<FormState | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sharedDraft, setSharedDraft] = useState<string[]>([]);
  // Só o que a pessoa editou e ainda não salvou; o resto vem sempre do servidor
  const [itemDrafts, setItemDrafts] = useState<Record<string, string[]>>({});

  const api = useCallback(async (url: string, options: RequestInit = {}) => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('auth_token');
    if (token) headers.Authorization = `Bearer ${token}`;
    if (selectedTenantId) headers['X-Tenant-Id'] = selectedTenantId;
    const response = await fetch(url, { ...options, headers: { ...headers, ...(options.headers as any) } });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
    return data;
  }, [selectedTenantId]);

  const loadList = useCallback(async () => {
    try {
      setList(await api('/api/follow-ups'));
    } catch (error: any) {
      toast.error(error.message);
    }
  }, [api]);

  const loadCurrent = useCallback(async (id: string) => {
    try {
      const data: FollowUp = await api(`/api/follow-ups/${id}`);
      setCurrent(data);
      setSharedDraft(data.sharedMessages || []);
    } catch (error: any) {
      toast.error(error.message);
    }
  }, [api]);

  useEffect(() => {
    loadList();
    api('/api/campaigns/sessions').then(setSessions).catch(() => undefined);
    api('/api/follow-ups/options').then(data => setModels(data.models)).catch(() => undefined);
  }, [api, loadList]);

  // Enquanto a IA escreve ou o envio corre, a tela acompanha sozinha
  useEffect(() => {
    if (!current || !['GENERATING', 'SENDING'].includes(current.status)) return;
    const timer = setInterval(() => loadCurrent(current.id), 5000);
    return () => clearInterval(timer);
  }, [current, loadCurrent]);

  const run = async (action: () => Promise<any>, success?: string) => {
    setBusy(true);
    try {
      await action();
      if (success) toast.success(success);
    } catch (error: any) {
      toast.error(error.message, { duration: 8000 });
    } finally {
      setBusy(false);
    }
  };

  const saveForm = () => run(async () => {
    if (!form) return;
    const period = resolvePeriod(form);
    if (isNaN(period.from.getTime()) || isNaN(period.to.getTime())) throw new Error(t('fu.errPeriod'));
    const body = {
      ...form,
      periodFrom: period.from.toISOString(),
      periodTo: period.to.toISOString(),
      periodRanges: period.ranges ? period.ranges.map(r => ({ from: r.from.toISOString(), to: r.to.toISOString() })) : null,
      maxDelaySeconds: form.maxDelayMinutes * 60,
      sendFromSessionName: form.sendFromSessionName || null,
      fallbackSessionName: form.fallbackSessionName || null,
    };
    if (editingId) {
      const result = await api(`/api/follow-ups/${editingId}`, { method: 'PUT', body: JSON.stringify(body) });
      if (result.audienceRebuilt) toast(t('fu.listRebuilt'));
      else if (result.messagesReset) toast(t('fu.messagesReset'));
      await loadCurrent(editingId);
    } else {
      const created = await api('/api/follow-ups', { method: 'POST', body: JSON.stringify(body) });
      await loadCurrent(created.id);
    }
    setForm(null);
    setEditingId(null);
    loadList();
  }, editingId ? t('fu.saved') : t('fu.created'));

  const action = (path: string, success: string, method = 'POST') => current && run(async () => {
    await api(`/api/follow-ups/${current.id}${path}`, { method });
    await loadCurrent(current.id);
    loadList();
  }, success);

  const dropDraft = (itemId: string) => setItemDrafts(prev => {
    const next = { ...prev };
    delete next[itemId];
    return next;
  });

  const saveItem = (item: FollowUpItem, patch: { included?: boolean; messages?: string[] }) => current && run(async () => {
    await api(`/api/follow-ups/${current.id}/items/${item.id}`, { method: 'PATCH', body: JSON.stringify(patch) });
    if (patch.messages) dropDraft(item.id);
    await loadCurrent(current.id);
  });

  const regenerateItem = (item: FollowUpItem) => current && run(async () => {
    await api(`/api/follow-ups/${current.id}/items/${item.id}/regenerate`, { method: 'POST' });
    dropDraft(item.id);
    await loadCurrent(current.id);
  });

  const saveShared = () => current && run(async () => {
    await api(`/api/follow-ups/${current.id}/shared-messages`, { method: 'PUT', body: JSON.stringify({ messages: sharedDraft }) });
    await loadCurrent(current.id);
  }, t('fu.saved'));

  const remove = (followUp: FollowUp) => {
    if (!confirm(t('fu.confirmDelete', { title: followUp.name }))) return;
    run(async () => {
      await api(`/api/follow-ups/${followUp.id}`, { method: 'DELETE' });
      if (current?.id === followUp.id) setCurrent(null);
      loadList();
    }, t('fu.deleted'));
  };

  const fallbackOptions = sessions.filter(s => s.provider && s.provider !== 'META');
  const ago = (iso: string) => {
    const hours = Math.round((Date.now() - new Date(iso).getTime()) / 3600000);
    return hours < 48 ? t('fu.hoursAgo', { n: hours }) : t('fu.daysAgo', { n: Math.round(hours / 24) });
  };
  // Data e hora exatas: "há 6 dias" sozinho confunde quem escolheu um dia específico
  const when = (iso: string) => new Date(iso).toLocaleString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
  });
  const periodText = (followUp: FollowUp) => {
    if (followUp.periodRanges?.length) {
      return t('fu.periodShownDays', {
        days: followUp.periodRanges.map(r => new Date(r.from).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })).join('، '),
      });
    }
    const from = new Date(followUp.periodFrom);
    const to = new Date(followUp.periodTo);
    const singleDay = toLocalInput(from).endsWith('T00:00') && toDateInput(from) === toDateInput(to)
      && to.getHours() === 23 && to.getMinutes() === 59;
    return singleDay
      ? t('fu.periodShownDay', { day: from.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) })
      : t('fu.periodShownRange', { from: from.toLocaleString(), to: to.toLocaleString() });
  };
  const editable = current && ['DRAFT', 'READY'].includes(current.status);
  const items = current?.items || [];
  const count = (status: string) => items.filter(i => i.status === status).length;
  const included = items.filter(i => i.included && i.status !== 'SKIPPED');

  // ------------------------------------------------------------------ formulário

  const formModal = form && (
    <Portal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto p-6 space-y-4">
          <h2 className="text-xl font-bold text-gray-900">{editingId ? t('fu.editTitle') : t('fu.newTitle')}</h2>

          <label className="block">
            <span className="text-sm font-semibold text-gray-700">{t('fu.name')}</span>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md" placeholder={t('fu.namePlaceholder')} />
          </label>

          <div>
            <span className="text-sm font-semibold text-gray-700">{t('fu.mode')}</span>
            <div className="mt-1 grid sm:grid-cols-2 gap-2">
              {(['PERSONALIZED', 'SHARED'] as const).map(mode => (
                <button key={mode} type="button" onClick={() => setForm({ ...form, mode })}
                  className={`text-start p-3 rounded-lg border ${form.mode === mode ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                  <div className="font-medium text-sm">{t(`fu.mode.${mode}`)}</div>
                  <div className="text-xs text-gray-600 mt-1">{t(`fu.mode.${mode}.help`)}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className="text-sm font-semibold text-gray-700">{t('fu.period')}</span>
            <div className="flex flex-wrap gap-2 mt-1 mb-2">
              {(['DAYS_AGO', 'DATE', 'DATES', 'RANGE'] as const).map(periodMode => (
                <button key={periodMode} type="button" onClick={() => setForm({ ...form, periodMode })}
                  className={`px-3 py-1.5 text-sm rounded-md border ${form.periodMode === periodMode ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-gray-300'}`}>
                  {t(`fu.periodMode.${periodMode}`)}
                </button>
              ))}
            </div>

            {form.periodMode === 'DAYS_AGO' && (
              <div className="flex flex-wrap items-center gap-2">
                {[1, 3, 7, 14, 30].map(days => (
                  <button key={days} type="button" onClick={() => setForm({ ...form, daysAgo: days })}
                    className={`px-2 py-1 text-xs rounded border ${form.daysAgo === days ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>
                    {t('fu.daysAgoOption', { n: days })}
                  </button>
                ))}
                <input type="number" min={1} max={365} value={form.daysAgo}
                  onChange={e => setForm({ ...form, daysAgo: Math.min(Math.max(Number(e.target.value) || 1, 1), 365) })}
                  className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm" />
                <span className="text-sm text-gray-600">{t('fu.days')}</span>
              </div>
            )}

            {form.periodMode === 'DATE' && (
              <input type="date" value={form.day} max={toDateInput(new Date())} onChange={e => setForm({ ...form, day: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-md" />
            )}

            {form.periodMode === 'DATES' && (
              <div className="space-y-2">
                <input type="date" value="" max={toDateInput(new Date())}
                  onChange={e => {
                    const picked = e.target.value;
                    if (picked && !form.days.includes(picked)) setForm({ ...form, days: [...form.days, picked].sort() });
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md" />
                <p className="text-xs text-gray-500">{t('fu.daysPickHelp')}</p>
                <div className="flex flex-wrap gap-2">
                  {form.days.map(day => (
                    <span key={day} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded-full text-sm">
                      {new Date(`${day}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
                      <button type="button" aria-label="remove" onClick={() => setForm({ ...form, days: form.days.filter(d => d !== day) })}
                        className="text-blue-700 hover:text-red-600">×</button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {form.periodMode === 'RANGE' && (
              <div className="grid sm:grid-cols-2 gap-2">
                <input type="datetime-local" value={form.periodFrom} onChange={e => setForm({ ...form, periodFrom: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-md" />
                <input type="datetime-local" value={form.periodTo} onChange={e => setForm({ ...form, periodTo: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-md" />
              </div>
            )}

            <p className="text-sm text-blue-800 bg-blue-50 rounded px-2 py-1 mt-2">
              {form.periodMode === 'DATES'
                ? (form.days.length
                  ? t('fu.periodExplainDays', { days: form.days.map(d => new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'long' })).join('، ') })
                  : t('fu.daysNone'))
                : form.periodMode === 'RANGE'
                ? t('fu.periodExplainRange', {
                  from: new Date(form.periodFrom).toLocaleString(),
                  to: new Date(form.periodTo).toLocaleString(),
                })
                : t('fu.periodExplainDay', {
                  day: new Date(`${form.periodMode === 'DATE' ? form.day : daysAgoDate(form.daysAgo)}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' }),
                })}
            </p>
            <label className="flex items-start gap-2 mt-3 p-2 rounded-md border border-gray-200 bg-gray-50">
              <input type="checkbox" className="mt-1" checked={form.lastWordFromCustomer}
                onChange={e => setForm({ ...form, lastWordFromCustomer: e.target.checked })} />
              <span>
                <span className="block text-sm font-medium text-gray-800">{t('fu.lastWord')}</span>
                <span className="block text-xs text-gray-600">{t('fu.lastWordHelp')}</span>
              </span>
            </label>
            <p className="text-xs text-gray-500 mt-1">{t('fu.periodHelp')}</p>
          </div>

          <label className="block">
            <span className="text-sm font-semibold text-gray-700">{t('fu.brief')}</span>
            <textarea dir="auto" rows={4} value={form.instructions} onChange={e => setForm({ ...form, instructions: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder={t('fu.briefPlaceholder')} />
            <span className="text-xs text-gray-500">{t('fu.briefHelp')}</span>
          </label>


          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-semibold text-gray-700">{t('fu.maxDelay')}</span>
              <select value={form.maxDelayMinutes} onChange={e => setForm({ ...form, maxDelayMinutes: Number(e.target.value) })}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md">
                {[1, 2, 3, 4, 5, 6, 7].map(n => <option key={n} value={n}>{t('fu.minutes', { n })}</option>)}
              </select>
              <span className="text-xs text-gray-500">{t('fu.maxDelayHelp')}</span>
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-gray-700">{t('fu.aiProvider')}</span>
              <select value={form.aiProvider}
                onChange={e => {
                  const provider = e.target.value as 'OPENAI' | 'GROQ' | 'CLAUDE';
                  setForm({ ...form, aiProvider: provider, aiModel: models[provider]?.[0] || '' });
                }}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md">
                <option value="OPENAI">OpenAI</option>
                <option value="GROQ">Groq</option>
                <option value="CLAUDE">Claude (Anthropic)</option>
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-gray-700">{t('fu.aiModel')}</span>
              <select value={form.aiModel} onChange={e => setForm({ ...form, aiModel: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md">
                {(models[form.aiProvider] || []).map(model => <option key={model} value={model}>{model}</option>)}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="text-sm font-semibold text-gray-700">{t('fu.sendFrom')}</span>
            <select value={form.sendFromSessionName} onChange={e => setForm({ ...form, sendFromSessionName: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md">
              <option value="">{t('fu.sendFromAuto')}</option>
              {sessions.map(s => <option key={s.name} value={s.name}>{s.displayName || s.name} ({s.provider === 'META' ? 'Cloud API' : s.provider})</option>)}
            </select>
            <span className="text-xs text-gray-500">
              {sessions.find(s => s.name === form.sendFromSessionName)?.provider === 'META' ? t('fu.sendFromMetaHelp') : t('fu.sendFromHelp')}
            </span>
          </label>

          {(!form.sendFromSessionName || sessions.find(s => s.name === form.sendFromSessionName)?.provider === 'META') && (
            <label className="block">
              <span className="text-sm font-semibold text-gray-700">{t('fu.fallback')}</span>
              <select value={form.fallbackSessionName} onChange={e => setForm({ ...form, fallbackSessionName: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md">
                <option value="">{t('fu.fallbackNone')}</option>
                {fallbackOptions.map(s => <option key={s.name} value={s.name}>{s.displayName || s.name} ({s.provider})</option>)}
              </select>
              <span className="text-xs text-gray-500">{t('fu.fallbackHelp')}</span>
            </label>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => { setForm(null); setEditingId(null); }}
              className="px-4 py-2 bg-gray-100 rounded-md">{t('common.cancel')}</button>
            <button type="button" disabled={busy || !form.name.trim()} onClick={saveForm}
              className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">
              {busy ? t('fu.working') : editingId ? t('common.save') : t('fu.createAndFind')}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );

  // ------------------------------------------------------------------ lista

  if (!current) {
    return (
      <div>
        <Header title={t('fu.title')} subtitle={t('fu.subtitle')}
          actions={<button onClick={() => { setEditingId(null); setForm(emptyForm()); }}
            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium">+ {t('fu.new')}</button>} />
        <div className="p-6">
          {list.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">{t('fu.empty')}</div>
          ) : (
            <div className="bg-white rounded-lg shadow divide-y">
              {list.map(followUp => {
                const c = followUp.counts || {};
                const total = Object.values(c).reduce((a, b) => a + b, 0);
                return (
                  <div key={followUp.id} className="p-4 flex flex-wrap items-center gap-3 justify-between">
                    <button className="text-start flex-1 min-w-0" onClick={() => loadCurrent(followUp.id)}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{followUp.name}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_STYLE[followUp.status]}`}>{t(`fu.status.${followUp.status}`)}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {t(`fu.mode.${followUp.mode}`)} · {new Date(followUp.periodFrom).toLocaleDateString()} → {new Date(followUp.periodTo).toLocaleDateString()}
                        {' · '}{t('fu.summary', { total, sent: c.SENT || 0, replied: c.REPLIED || 0 })}
                      </div>
                    </button>
                    <div className="flex gap-2">
                      <button onClick={() => loadCurrent(followUp.id)} className="px-3 py-1 text-sm bg-blue-600 text-white rounded">{t('fu.open')}</button>
                      {followUp.status !== 'SENDING' && (
                        <button onClick={() => remove(followUp)} className="px-3 py-1 text-sm bg-red-600 text-white rounded">{t('fu.delete')}</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {formModal}
      </div>
    );
  }

  // ------------------------------------------------------------------ detalhe

  const statusBadge = (status: string) => (
    <span className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${STATUS_STYLE[status] || 'bg-gray-100'}`}>{t(`fu.item.${status}`)}</span>
  );

  return (
    <div>
      <Header title={current.name} subtitle={t('fu.subtitle')}
        actions={<button onClick={() => { setCurrent(null); loadList(); }} className="px-4 py-2 bg-gray-100 rounded-lg text-sm">{t('fu.back')}</button>} />

      <div className="p-6 space-y-4">
        {/* Resumo e ações */}
        <div className="bg-white rounded-lg shadow p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_STYLE[current.status]}`}>{t(`fu.status.${current.status}`)}</span>
            <span className="text-sm text-gray-600">
              {t(`fu.mode.${current.mode}`)} · {current.aiProvider === 'GROQ' ? 'Groq' : current.aiProvider === 'CLAUDE' ? 'Claude' : 'OpenAI'} {current.aiModel}
              {' · '}{current.sendFromSessionName ? t('fu.fromNumber', { number: current.sendFromSessionName }) : t('fu.sendFromAuto')}
              {' · '}{t('fu.upTo', { n: Math.round(current.maxDelaySeconds / 60) })}
            </span>
          </div>
          <div className="text-sm text-gray-700">
            {t('fu.counts', { total: items.length, included: included.length, skipped: count('SKIPPED') })}
            {items.length > 0 && (
              <span className="block text-xs text-gray-500 mt-0.5">
                {t('fu.byNumber')}{' '}
                {Object.entries(items.reduce<Record<string, number>>((acc, i) => {
                  const key = i.sourceSessionName || '—';
                  acc[key] = (acc[key] || 0) + 1;
                  return acc;
                }, {})).map(([number, n]) => `${number} (${n})`).join(' · ')}
              </span>
            )}
            {['SENDING', 'PAUSED', 'DONE', 'CANCELLED'].includes(current.status) && (
              <> · {t('fu.progress', { sent: count('SENT'), replied: count('REPLIED'), failed: count('FAILED'), left: count('QUEUED') + count('SENDING') })}</>
            )}
            {current.status === 'GENERATING' && <> · {t('fu.writing', { done: count('READY') + count('FAILED'), total: included.length })}</>}
          </div>
          <p className="text-sm text-blue-800 bg-blue-50 rounded px-2 py-1">
            📅 {periodText(current)}
            {current.lastWordFromCustomer && <span className="block">💬 {t('fu.lastWordShown')}</span>}
          </p>
          {current.waitingReason && <p className="text-sm text-amber-700">⏳ {current.waitingReason}</p>}

          <div className="flex flex-wrap gap-2">
            {editable && (
              <>
                <button disabled={busy} onClick={() => { setEditingId(current.id); setForm(formFrom(current)); }}
                  className="px-3 py-1.5 text-sm bg-gray-100 rounded">{t('fu.editSettings')}</button>
                <button disabled={busy} onClick={() => action('/audience', t('fu.listRebuilt'))}
                  className="px-3 py-1.5 text-sm bg-gray-100 rounded">{t('fu.refreshList')}</button>
                <button disabled={busy || included.length === 0} onClick={() => action('/generate', t('fu.writingStarted'))}
                  className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded disabled:opacity-50">
                  ✨ {current.status === 'READY' ? t('fu.rewriteAll') : t('fu.write')}
                </button>
              </>
            )}
            {current.status === 'READY' && (
              <button disabled={busy} onClick={() => confirm(t('fu.confirmStart', { n: included.filter(i => i.status === 'READY').length })) && action('/start', t('fu.started'))}
                className="px-3 py-1.5 text-sm bg-green-600 text-white rounded">▶ {t('fu.start')}</button>
            )}
            {current.status === 'SENDING' && <button disabled={busy} onClick={() => action('/pause', t('fu.paused'))} className="px-3 py-1.5 text-sm bg-yellow-500 text-white rounded">{t('fu.pause')}</button>}
            {current.status === 'PAUSED' && <button disabled={busy} onClick={() => action('/resume', t('fu.resumed'))} className="px-3 py-1.5 text-sm bg-green-600 text-white rounded">{t('fu.resume')}</button>}
            {['SENDING', 'PAUSED'].includes(current.status) && (
              <button disabled={busy} onClick={() => confirm(t('fu.confirmStop')) && action('/cancel', t('fu.stopped'))} className="px-3 py-1.5 text-sm bg-red-600 text-white rounded">{t('fu.stop')}</button>
            )}
          </div>

          {current.greeting && (
            <p dir="auto" className="text-sm text-gray-600"><span className="font-medium">{t('fu.greeting')}:</span> {current.greeting}</p>
          )}
        </div>

        {/* Mensagens para todos */}
        {current.mode === 'SHARED' && (current.sharedMessages || editable) && (
          <div className="bg-white rounded-lg shadow p-4 space-y-2">
            <h3 className="font-semibold text-gray-900">{t('fu.sharedTitle')}</h3>
            {sharedDraft.length === 0 && <p className="text-sm text-gray-500">{t('fu.sharedEmpty')}</p>}
            {editable && (
              <div className="flex flex-wrap gap-2">
                {READY_MESSAGES.map(ready => (
                  <button key={ready.key} type="button" onClick={() => setSharedDraft([ready.text])}
                    className="px-3 py-1.5 text-sm bg-emerald-50 border border-emerald-300 text-emerald-800 rounded">
                    📋 {t(`fu.ready.${ready.key}`)}
                  </button>
                ))}
                <button type="button" onClick={() => setSharedDraft([''])} className="px-3 py-1.5 text-sm bg-gray-100 rounded">✏️ {t('fu.writeMyself')}</button>
              </div>
            )}
            {sharedDraft.map((message, index) => (
              <textarea key={index} dir="auto" rows={3} value={message} disabled={!editable}
                onChange={e => setSharedDraft(sharedDraft.map((m, i) => (i === index ? e.target.value : m)))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
            ))}
            {editable && sharedDraft.length > 0 && (
              <button disabled={busy} onClick={saveShared} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded">{t('fu.saveMessages')}</button>
            )}
          </div>
        )}

        {/* Clientes */}
        <div className="bg-white rounded-lg shadow divide-y">
          {items.length === 0 && <div className="p-6 text-center text-gray-500">{t('fu.noCustomers')}</div>}
          {items.map(item => {
            const drafts = itemDrafts[item.id] ?? item.messages ?? [];
            const changed = JSON.stringify(drafts) !== JSON.stringify(item.messages || []);
            // Quem ainda não recebeu nada pode ter a mensagem mudada, mesmo com o envio em andamento
            const itemEditable = item.nextIndex === 0
              && ['PENDING', 'READY', 'FAILED', 'QUEUED'].includes(item.status)
              && ['DRAFT', 'READY', 'PAUSED', 'SENDING'].includes(current.status);
            return (
              <div key={item.id} className={`p-4 space-y-2 ${item.included ? '' : 'opacity-60'}`}>
                <div className="flex flex-wrap items-center gap-2 justify-between">
                  <div className="min-w-0">
                    <span className="font-medium text-gray-900" dir="auto">{item.contactName || item.phone}</span>
                    <span className="text-xs text-gray-500 ms-2" dir="ltr">+{item.phone}</span>
                    <span className="block text-xs text-gray-500">
                      {item.sourceSessionName && t('fu.chatOn', { number: item.sourceSessionName })}
                      {item.sessionName && <> · {t('fu.sendsFrom', { number: item.sessionName })}</>}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {statusBadge(item.status)}
                    {editable && item.status !== 'SKIPPED' && (
                      <label className="text-xs flex items-center gap-1">
                        <input type="checkbox" checked={item.included} onChange={e => saveItem(item, { included: e.target.checked })} />
                        {t('fu.include')}
                      </label>
                    )}
                    {!editable && itemEditable && item.status === 'QUEUED' && (
                      <button disabled={busy} onClick={() => confirm(t('fu.confirmLeaveOut')) && saveItem(item, { included: false })}
                        className="text-xs px-2 py-0.5 rounded border border-gray-300 hover:bg-gray-50">{t('fu.leaveOut')}</button>
                    )}
                  </div>
                </div>

                <p dir="auto" className="text-sm text-gray-600 bg-gray-50 rounded px-2 py-1">
                  💬 {item.lastInboundText || '—'}
                  <span className="block text-xs text-gray-500 mt-0.5">{t('fu.lastMessageAt', { when: when(item.lastInboundAt), ago: ago(item.lastInboundAt) })}</span>
                </p>
                {item.skipReason && <p className="text-xs text-gray-500">{item.skipReason}</p>}
                {item.error && <p className="text-xs text-red-600">{item.error}</p>}

                {current.mode === 'PERSONALIZED' && item.status !== 'SKIPPED' && (drafts.length > 0 || item.status === 'GENERATING') && (
                  <div className="space-y-2">
                    {item.status === 'GENERATING' && <p className="text-xs text-purple-700">✨ {t('fu.writingOne')}</p>}
                    {drafts.map((message, index) => (
                      <textarea key={index} dir="auto" rows={3} value={message} disabled={!itemEditable}
                        onChange={e => setItemDrafts(prev => ({ ...prev, [item.id]: drafts.map((m, i) => (i === index ? e.target.value : m)) }))}
                        className={`w-full px-3 py-2 border rounded-md text-sm ${index < item.nextIndex - (current.greeting ? 1 : 0) ? 'border-emerald-300 bg-emerald-50' : 'border-gray-300'}`} />
                    ))}
                    {itemEditable && (
                      <div className="flex flex-wrap items-center gap-2">
                        {changed && <button disabled={busy} onClick={() => saveItem(item, { messages: drafts })} className="px-3 py-1 text-xs bg-blue-600 text-white rounded">{t('fu.saveMessages')}</button>}
                        {changed && current.status === 'SENDING' && <span className="text-xs text-amber-700">{t('fu.editWhileSending')}</span>}
                        <button disabled={busy} onClick={() => regenerateItem(item)} className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded">✨ {t('fu.rewriteOne')}</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {formModal}
    </div>
  );
}
